/**
 * Frontend Tool Handlers — DocEngine Excel 高级操作
 *
 * 处理的工具：
 * - compare_excel_documents → 版本对比
 * - scrub_excel_document → 痕迹清洗
 * - redact_excel_document → 数据脱敏
 * - merge_excel_documents → 多文件合并
 * - fill_excel_template → 模板填充
 * - extract_excel_formulas → 公式提取
 * - deduplicate_excel_data → 数据去重
 */

import {
  registerFrontendTool,
  authedPost,
  triggerBase64Download,
  type FrontendToolContext,
} from '../frontend-dispatcher';
import { readFileAsBase64 } from '../../hooks/useFileSystem';

const EXCEL_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// ─── compare_excel_documents ────────────────
registerFrontendTool('compare_excel_documents', async (args, ctx) => {
  if (!args.originalFile || !args.revisedFile) return `[Error: Missing originalFile or revisedFile]`;

  ctx.setMessageStatus('searching', `🔍 正在对比 Excel 版本差异...`);

  const origData = await readFileAsBase64(ctx.localDirHandle, args.originalFile.filePath);
  const revData = await readFileAsBase64(ctx.localDirHandle, args.revisedFile.filePath);
  if (!origData || !revData) return `[Error: Failed to read comparison files]`;

  const resp = await authedPost(
    `${ctx.apiBaseUrl}/api/docengine/compare-excel`,
    {
      originalFileBase64: origData.base64,
      revisedFileBase64: revData.base64,
      sheetName: args.sheetName,
      highlightAdded: args.highlightAdded,
      highlightRemoved: args.highlightRemoved,
      highlightModified: args.highlightModified,
    },
    ctx.authToken,
  );

  if (!resp.ok) return `[Error: API failed: ${resp.statusText}]`;
  const result = await resp.json();

  if (result.success && result.base64) {
    const downloadName = args.originalFile.filePath.split('/').pop()?.replace(/\.xlsx$/i, '_Compared.xlsx') || 'Compared.xlsx';
    triggerBase64Download(result.base64, EXCEL_MIME, downloadName);
    return `[Excel 对比完成]:\n发现差异单元格: ${result.totalChanges || 0} 个\n✅ 对比结果文件已自动下载。`;
  }
  return `[Error: ${result.error}]`;
});

// ─── scrub_excel_document ───────────────────
registerFrontendTool('scrub_excel_document', async (args, ctx) => {
  if (!args.filePath) return `[Error: Missing filePath]`;

  ctx.setMessageStatus('searching', `🧹 正在清洗 Excel 痕迹与元数据...`);

  const fileData = await readFileAsBase64(ctx.localDirHandle, args.filePath);
  if (!fileData) return `[Error: Failed to read file ${args.filePath}]`;

  const resp = await authedPost(
    `${ctx.apiBaseUrl}/api/docengine/scrub-excel`,
    { fileBase64: fileData.base64, options: args.options },
    ctx.authToken,
  );

  if (!resp.ok) return `[Error: API failed: ${resp.statusText}]`;
  const result = await resp.json();

  if (result.success && result.base64) {
    const downloadName = args.filePath.split('/').pop()?.replace(/\.xlsx$/i, '_Scrubbed.xlsx') || 'Scrubbed.xlsx';
    triggerBase64Download(result.base64, EXCEL_MIME, downloadName);
    return `[Excel 清洗完成 ${args.filePath}]:\n已成功移除指定的隐藏项与元数据。\n✅ 清洗后的文件已自动下载。`;
  }
  return `[Error: ${result.error}]`;
});

// ─── redact_excel_document ──────────────────
registerFrontendTool('redact_excel_document', async (args, ctx) => {
  if (!args.filePath) return `[Error: Missing filePath]`;

  ctx.setMessageStatus('searching', `🔒 正在对 Excel 进行数据脱敏...`);

  const fileData = await readFileAsBase64(ctx.localDirHandle, args.filePath);
  if (!fileData) return `[Error: Failed to read file ${args.filePath}]`;

  const resp = await authedPost(
    `${ctx.apiBaseUrl}/api/docengine/redact-excel`,
    { fileBase64: fileData.base64, redactions: args.redactions, replacementStyle: args.replacementStyle },
    ctx.authToken,
  );

  if (!resp.ok) return `[Error: API failed: ${resp.statusText}]`;
  const result = await resp.json();

  if (result.success && result.base64) {
    const downloadName = args.filePath.split('/').pop()?.replace(/\.xlsx$/i, '_Redacted.xlsx') || 'Redacted.xlsx';
    triggerBase64Download(result.base64, EXCEL_MIME, downloadName);
    return `[Excel 数据脱敏完成 ${args.filePath}]:\n成功脱敏单元格: ${result.cellsRedacted || 0} 个\n✅ 脱敏后的文件已自动下载。`;
  }
  return `[Error: ${result.error}]`;
});

// ─── merge_excel_documents ──────────────────
registerFrontendTool('merge_excel_documents', async (args, ctx) => {
  if (!args.files || !Array.isArray(args.files) || args.files.length < 2) {
    return `[Error: Need at least 2 files to merge]`;
  }

  ctx.setMessageStatus('searching', `📎 正在合并 ${args.files.length} 份 Excel 文件...`);

  const filesBase64: string[] = [];
  for (const f of args.files) {
    const fileData = await readFileAsBase64(ctx.localDirHandle, f.filePath);
    if (!fileData) return `[Error: Failed to read one or more files for merging]`;
    filesBase64.push(fileData.base64);
  }

  const resp = await authedPost(
    `${ctx.apiBaseUrl}/api/docengine/merge-excel`,
    { filesBase64, mergeMode: args.mergeMode, targetSheetName: args.targetSheetName, deduplicateHeaders: args.deduplicateHeaders },
    ctx.authToken,
  );

  if (!resp.ok) return `[Error: API failed: ${resp.statusText}]`;
  const result = await resp.json();

  if (result.success && result.base64) {
    triggerBase64Download(result.base64, EXCEL_MIME, `Merged_Excel_${Date.now()}.xlsx`);
    return `[Excel 文件合并完成]:\n参与合并文件数: ${result.filesMerged || args.files.length} 份\n✅ 合并后的文件已自动下载。`;
  }
  return `[Error: ${result.error}]`;
});

// ─── fill_excel_template ────────────────────
registerFrontendTool('fill_excel_template', async (args, ctx) => {
  if (!args.filePath) return `[Error: Missing filePath]`;

  ctx.setMessageStatus('searching', `📝 正在为您执行 Excel 模板填充...`);

  const fileData = await readFileAsBase64(ctx.localDirHandle, args.filePath);
  if (!fileData) return `[Error: Failed to read file ${args.filePath}]`;

  const resp = await authedPost(
    `${ctx.apiBaseUrl}/api/docengine/fill-excel-template`,
    { fileBase64: fileData.base64, fields: args.fields, tables: args.tables },
    ctx.authToken,
  );

  if (!resp.ok) return `[Error: API failed: ${resp.statusText}]`;
  const result = await resp.json();

  if (result.success && result.base64) {
    const downloadName = args.filePath.split('/').pop()?.replace(/\.xlsx$/i, '_Filled.xlsx') || 'Filled.xlsx';
    triggerBase64Download(result.base64, EXCEL_MIME, downloadName);
    return `[Excel 模板填充完成 ${args.filePath}]:\n替换占位符: ${result.fieldsReplaced || 0} 处\n注入数据行: ${result.rowsInserted || 0} 行\n✅ 填充后的文件已自动下载。`;
  }
  return `[Error: ${result.error}]`;
});

// ─── extract_excel_formulas ─────────────────
registerFrontendTool('extract_excel_formulas', async (args, ctx) => {
  if (!args.filePath) return `[Error: Missing filePath]`;

  ctx.setMessageStatus('searching', `🔬 正在提取 Excel 模型底层公式...`);

  const fileData = await readFileAsBase64(ctx.localDirHandle, args.filePath);
  if (!fileData) return `[Error: Failed to read file ${args.filePath}]`;

  const resp = await authedPost(
    `${ctx.apiBaseUrl}/api/docengine/extract-excel-formulas`,
    { fileBase64: fileData.base64, sheetName: args.sheetName, includeNamedRanges: args.includeNamedRanges, includeExternalLinks: args.includeExternalLinks },
    ctx.authToken,
  );

  if (!resp.ok) return `[Error: API failed: ${resp.statusText}]`;
  const result = await resp.json();

  if (result.success) {
    const stats = result.statistics || {};
    return `[Excel 公式提取完成 ${args.filePath}]:\n` +
      `包含独立公式的单元格数: ${stats.totalFormulas || 0} 个\n` +
      `使用到的函数类型: ${(stats.uniqueFunctions || []).join(', ') || '无'}\n` +
      (stats.hasCircularReference ? `⚠️ 警告: 检测到循环引用!\n` : '') +
      `\n[提取的底层逻辑结构数据供参考]:\n${JSON.stringify({ sheets: result.sheets, namedRanges: result.namedRanges, externalLinks: result.externalLinks }, null, 2)}`;
  }
  return `[Error: ${result.error}]`;
});

// ─── deduplicate_excel_data ─────────────────
registerFrontendTool('deduplicate_excel_data', async (args, ctx) => {
  if (!args.filePath) return `[Error: Missing filePath]`;

  ctx.setMessageStatus('searching', `🗑️ 正在对 Excel 数据去重清洗...`);

  const fileData = await readFileAsBase64(ctx.localDirHandle, args.filePath);
  if (!fileData) return `[Error: Failed to read file ${args.filePath}]`;

  const resp = await authedPost(
    `${ctx.apiBaseUrl}/api/docengine/deduplicate-excel`,
    { fileBase64: fileData.base64, sheetName: args.sheetName, keyColumns: args.keyColumns, addDuplicateCount: args.addDuplicateCount, keepAllColumns: args.keepAllColumns },
    ctx.authToken,
  );

  if (!resp.ok) return `[Error: API failed: ${resp.statusText}]`;
  const result = await resp.json();

  if (result.success && result.base64) {
    const downloadName = args.filePath.split('/').pop()?.replace(/\.xlsx$/i, '_Deduplicated.xlsx') || 'Deduplicated.xlsx';
    triggerBase64Download(result.base64, EXCEL_MIME, downloadName);
    return `[Excel 数据去重完成 ${args.filePath}]:\n去重前总行数: ${result.totalRowsBefore || 0}\n去重后总行数: ${result.totalRowsAfter || 0}\n成功移除重复行: ${result.duplicatesRemoved || 0} 行\n✅ 清洗后的文件已自动下载。`;
  }
  return `[Error: ${result.error}]`;
});
